import * as React from "react";
import { observer, MobXProviderContext } from "mobx-react";
import { UitkButtonToggle } from "uitk-react-button-toggle";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { FlexComponent } from "src/typings/flexFramework/FlexComponent";
import { FlexComponentNameToWizardStateConfig } from "components/flexComponents/StorefrontWizardRegion/FlexComponentNameToWizardStateConfig";
import { LobNavigationProps } from "components/flexComponents/StorefrontWizardRegion/LobNavigation";
import { QueryObject, RouteStore } from "bernie-core";
import { Navigation } from "src/stores/wizard/config";
import { ClientWizardStateStore } from "src/stores/wizard/ClientWizardStateStore";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { useLocalization } from "@shared-ui/localization-context";
import { scrimValues } from "./typings";
import { LobNavigationScrims } from "./LobNavigationScrims";

export const LobNavigation: React.FunctionComponent<LobNavigationProps> = observer(({ lobs }) => {
  const wizardState: ClientWizardStateStore = React.useContext(MobXProviderContext).wizardState;
  const routeStore: RouteStore = React.useContext(MobXProviderContext).routeStore;
  const keyHelper = new ItemKeyHelper("storefrontWizardNavigation");
  const defaultLobNavConfig: Navigation = {
    tabIcon: "help",
    tabTitleToken: "",
  };

  // No need for navigation in a single LOB pages
  if (lobs.length === 1) {
    return null;
  }

  // Check the route store, if there is no LOB query attached, get it from the wizardState default and redirect
  if (!routeStore.queryParams.pwaLob) {
    const { useDefaultLOB, defaultLob: pwaLob } = wizardState;

    if (useDefaultLOB) {
      routeStore.updateSearch({ pwaLob });
      wizardState.setUseDefaultLOB(false);
    } else {
      if (typeof window !== "undefined" && window.history) {
        window.history.back();
      }
    }
  }

  const lobQueryCombiner = (prevQuery: QueryObject, nextQuery: QueryObject): QueryObject => {
    delete prevQuery.pwaSubLOB;

    return { ...prevQuery, ...nextQuery };
  };

  const handleLobChange = (lobName: string, model: any) => () => {
    const { directLink } = model;

    if (directLink) {
      window.location.assign(directLink);

      return;
    }

    let queryParams: any = { pwaLob: lobName };
    const { subLOBState } = wizardState.lobStateMap[lobName];

    if (subLOBState) {
      queryParams = { pwaLob: lobName, pwaSubLOB: subLOBState };
    }
    routeStore.updateSearch(queryParams, {}, false, lobQueryCombiner);
  };

  const isTabChecked = (lobName: string): boolean => wizardState.activeLob === lobName;
  const { formatText } = useLocalization();

  const getLobIcon = (lobName: string): string => {
    // If there is no config for the lob it will use help icon as default
    let lobIcon = defaultLobNavConfig.tabIcon;
    if (wizardState[FlexComponentNameToWizardStateConfig[lobName]]) {
      lobIcon = wizardState[FlexComponentNameToWizardStateConfig[lobName]].config.navigation.tabIcon;
    }

    return lobIcon;
  };

  const getLabel = (lobName: string): string => {
    let label = defaultLobNavConfig.tabTitleToken;
    if (wizardState[FlexComponentNameToWizardStateConfig[lobName]]) {
      label = formatText(wizardState[FlexComponentNameToWizardStateConfig[lobName]].config.navigation.tabTitleToken);
    }

    return label;
  };

  const [scrimsToDisplay, setScrimToDisplay] = React.useState(scrimValues.RIGHT);

  const onLobNavigationScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const scrollLeftMin = 0;
    const { scrollLeft, offsetWidth, scrollWidth } = event.currentTarget;

    const displayLeft = scrollLeft > scrollLeftMin;
    const displayRight = scrollLeft + offsetWidth < scrollWidth;

    let currentScrims = scrimValues.RIGHT;

    if (displayLeft && displayRight) {
      currentScrims = scrimValues.BOTH;
    } else if (displayLeft) {
      currentScrims = scrimValues.LEFT;
    }

    setScrimToDisplay(currentScrims);
  };

  return (
    <div className="lobNavigationWrapper s-b-margin-one" role="tablist">
      <UitkLayoutFlex
        tag="ul"
        className="lobNavigation no-bullet uitk-scroll-horizontal"
        onScroll={onLobNavigationScroll}
        data-testid="lob-navigation"
      >
        {lobs.map(({ name, model }: FlexComponent, index) => (
          <UitkLayoutFlexItem
            className="lobItem all-r-margin-one all-b-margin-six"
            key={keyHelper.next()}
            aria-label={getLabel(name)}
            grow={1}
          >
            <li>
              <UitkButtonToggle
                checked={isTabChecked(name)}
                icon={getLobIcon(name)}
                id={name}
                label={getLabel(name)}
                onChange={handleLobChange(name, model)}
                aria-label={formatText("wizard.lob.tab.alttext", getLabel(name), index + 1, lobs.length)}
                role="tab"
                aria-selected={isTabChecked(name)}
              />
            </li>
          </UitkLayoutFlexItem>
        ))}
      </UitkLayoutFlex>

      <LobNavigationScrims scrimsToDisplay={scrimsToDisplay} />
    </div>
  );
});
